import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button, Checkbox, Field, Select, StatusPill, Tabs, Textarea } from '@bb/ui';
import type { StatusTone } from '@bb/ui';
import { useState } from 'react';
import { ApiError, type AdminApi, type PayloadPreview, type PayloadPreviewBody, type PreviewLinkChannel, type TestSendBody, type TestSendChannel, type TestSendResponse, type TestSendResult } from '../../../api/client.ts';
import { usePermissions } from '../../access/usePermissions.ts';
import { HumanView, type PreviewSample } from './PayloadPreviewPanel.tsx';
import { abortText, CHANNEL_LABELS, parseJsonObject } from './personalization.ts';
import { PreviewLinksSection, type PreviewLinkSource } from './PreviewLinksSection.tsx';
import { DEFAULT_PREVIEW_SEED, useTestUsers } from './ProfileSelector.tsx';
import { useTemplateVariables } from './useTemplateVariables.ts';

export interface MessageTestPanelProps {
  environmentId: string;
  /** The channels of this message a test can use; Android and iOS push give a choice. */
  channels: readonly TestSendChannel[];
  /** The content being edited per channel; without it the campaign's saved variant is used. */
  contentOf?: (channel: TestSendChannel) => Record<string, unknown> | undefined;
  /** The campaign: its saved variant, its trigger data samples and its preview links. */
  campaignId?: string;
  variantApiId?: string;
  /** Trigger data samples to edit as JSON; by default what the campaign's variables may read. */
  samples?: readonly PreviewSample[];
}

const PREVIEW_LINK_CHANNELS: readonly TestSendChannel[] = ['push_android', 'push_ios', 'content_card'];
const MAX_RECIPIENTS = 50;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SAMPLE_FIELDS: Record<PreviewSample, { label: string; send: 'eventPropertiesSample' | 'apiTriggerPropertiesSample' | 'contextSample'; preview: 'eventProperties' | 'apiTriggerProperties' | 'context' }> = {
  event_properties: { label: 'Event properties (JSON)', send: 'eventPropertiesSample', preview: 'eventProperties' },
  api_trigger_properties: { label: 'API trigger properties (JSON)', send: 'apiTriggerPropertiesSample', preview: 'apiTriggerProperties' },
  context: { label: 'Canvas context (JSON)', send: 'contextSample', preview: 'context' },
};
const SAMPLE_ORDER: readonly PreviewSample[] = ['event_properties', 'api_trigger_properties', 'context'];

const STATUS: Record<TestSendResult['status'], { label: string; tone: StatusTone }> = {
  accepted: { label: 'Accepted', tone: 'success' },
  no_reachable_device: { label: 'No reachable device', tone: 'warning' },
  aborted: { label: 'Not sent', tone: 'warning' },
  error: { label: 'Error', tone: 'danger' },
};

const DETAIL_TEXTS: Readonly<Record<string, string>> = {
  accepted_by_provider: 'The push provider accepted the notification.',
  card_created: 'The card was created: the app shows it after its next feed sync. It expires in 24 hours.',
  queued: 'The webhook request is queued for sending.',
  no_push_token: 'The user has no device with a push token on this platform.',
  unsubscribed: 'The user is unsubscribed from push.',
  token_unregistered: 'The provider says the device token is no longer valid: it was invalidated.',
  credential_missing: 'The app has no push credential for this platform.',
  invalid_content: 'The message content is not complete.',
  profile_not_found: 'No such profile in this environment.',
  profile_unavailable: 'The profile is deleted or being merged.',
  queue_unavailable: 'The send queue is not available: the webhook was not queued.',
};

/** A result's reason in words: the adapter's or provider's code with what it means. */
export function detailText(result: TestSendResult): string | undefined {
  const detail = result.detail;
  if (!detail) return undefined;
  if (detail.startsWith('retry_scheduled:')) return `The first attempt failed (${detail.slice('retry_scheduled:'.length)}): it is retried automatically.`;
  return DETAIL_TEXTS[detail] ?? (result.status === 'aborted' ? abortText({ reason: detail }) : undefined);
}

function sendErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'The test could not be sent.';
  if (error.code === 'test_recipient_not_allowed') {
    const ids = Array.isArray(error.problem.profileIds) ? (error.problem.profileIds as string[]).join(', ') : '';
    return `Nothing was sent: profiles outside content test groups need the messages.test_send.any_profile permission${ids ? ` (${ids})` : ''}.`;
  }
  if (error.code === 'test_group_not_found') return 'A chosen group is no longer a content test group of this environment: choose the groups again.';
  if (error.code === 'no_recipients') return 'The chosen groups have no users: add test users in Settings → Internal groups.';
  if (error.code === 'no_content') return 'The message has no content for this channel yet.';
  if (error.status === 429) return 'Too many test sends: wait a minute and try again.';
  if (error.status === 403) return 'Test send needs the messages.test_send permission.';
  return error.message;
}

/** Profile IDs typed one per line (or separated by commas), and the ones that are not IDs. */
function parseProfileIds(text: string): { ids: string[]; invalid: string[] } {
  const all = [...new Set(text.split(/[\s,]+/).filter(Boolean))];
  return { ids: all.filter((id) => UUID.test(id)), invalid: all.filter((id) => !UUID.test(id)) };
}

function ResultsTable({ response, labelOf }: { response: TestSendResponse; labelOf: (profileId: string) => string }) {
  const accepted = response.results.filter((r) => r.status === 'accepted').length;
  return (
    <section aria-label="Test send results">
      <p role="status">
        {CHANNEL_LABELS[response.channel]}: {accepted} of {response.results.length} accepted.
      </p>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">User</th>
            <th scope="col">Device</th>
            <th scope="col">Status</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {response.results.map((r, i) => (
            <tr key={`${r.profileId}/${r.deviceId ?? i}`}>
              <td>{labelOf(r.profileId)}</td>
              <td>{r.deviceId ? <code>{r.deviceId}</code> : '—'}</td>
              <td>
                <StatusPill tone={STATUS[r.status].tone}>{STATUS[r.status].label}</StatusPill>
              </td>
              <td>
                {detailText(r)} {r.detail && <code>{r.detail}</code>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="hint">Test sends are marked as tests and never count in reports.</p>
    </section>
  );
}

function TestSendForm({ environmentId, channel, source, overrides, samples, anyProfile }: { environmentId: string; channel: TestSendChannel; source: PreviewLinkSource | null; overrides?: Record<string, unknown>; samples: Partial<Record<PreviewSample, Record<string, unknown>>>; anyProfile: boolean }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const groups = useQuery({ queryKey: ['test-send-groups', environmentId], queryFn: async () => (await api.listInternalGroups(environmentId)).filter((g) => g.isContentTest) });
  const testUsers = useTestUsers(environmentId, true);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [profileIds, setProfileIds] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const other = parseProfileIds(anyProfile ? otherText : '');
  const allProfiles = [...new Set([...profileIds, ...other.ids])];
  const tooMany = allProfiles.length > MAX_RECIPIENTS;
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));
  const labelOf = (profileId: string) => {
    const m = testUsers.data?.find((u) => u.profileId === profileId);
    return m ? (m.externalId ?? m.emailMasked ?? profileId) : profileId;
  };

  const body: TestSendBody | null =
    source && (groupIds.length || allProfiles.length) && !other.invalid.length && !tooMany
      ? {
          channel,
          ...source,
          recipients: { ...(groupIds.length ? { testGroupIds: groupIds } : {}), ...(allProfiles.length ? { profileIds: allProfiles } : {}) },
          ...(overrides ? { attributeOverrides: overrides } : {}),
          ...Object.fromEntries(Object.entries(samples).map(([s, v]) => [SAMPLE_FIELDS[s as PreviewSample].send, v])),
        }
      : null;
  const send = useMutation({ mutationFn: (request: TestSendBody) => api.testSend(environmentId, request) });

  // The templating preview with the same overrides and samples, for the first chosen user (else a random one).
  const previewBody: PayloadPreviewBody | null = source
    ? {
        channel,
        ...('draftContent' in source && source.draftContent ? { content: source.draftContent } : {}),
        ...('messageRef' in source && source.messageRef ? { campaignId: source.messageRef.campaignId, ...(source.messageRef.variantApiId ? { variantApiId: source.messageRef.variantApiId } : {}) } : {}),
        profile: allProfiles[0] ? { kind: 'profile', profileId: allProfiles[0] } : { kind: 'random', seed: DEFAULT_PREVIEW_SEED },
        ...(overrides ? { attributeOverrides: overrides } : {}),
        ...Object.fromEntries(Object.entries(samples).map(([s, v]) => [SAMPLE_FIELDS[s as PreviewSample].preview, v])),
      }
    : null;
  const preview = useMutation({ mutationFn: (request: PayloadPreviewBody) => api.previewPayload(environmentId, request) });
  const previewed: PayloadPreview | undefined = preview.data;

  return (
    <div className="stack-form">
      <fieldset>
        <legend>Content test groups</legend>
        {groups.isPending && <p aria-busy="true">Loading groups…</p>}
        {groups.isError && <p role="alert">Internal groups could not be loaded.</p>}
        {groups.data?.length === 0 && <p className="hint">No content test groups yet: mark an internal group for content tests in Settings → Internal groups.</p>}
        {groups.data?.map((g) => (
          <Checkbox key={g.id} label={`${g.name} (${g.memberCount} ${g.memberCount === 1 ? 'user' : 'users'})`} checked={groupIds.includes(g.id)} onCheckedChange={(on) => setGroupIds((was) => toggle(was, g.id, on))} />
        ))}
      </fieldset>
      <fieldset>
        <legend>Test users</legend>
        {testUsers.isError && <p role="alert">Test users could not be loaded.</p>}
        {testUsers.data?.map((m) => (
          <Checkbox key={m.profileId} label={`${m.externalId ?? m.emailMasked ?? m.profileId} (${m.group})`} checked={profileIds.includes(m.profileId)} onCheckedChange={(on) => setProfileIds((was) => toggle(was, m.profileId, on))} />
        ))}
        {anyProfile ? (
          <Field label="Other profile IDs" hint="One profile ID per line: any profile of this environment, also outside test groups." error={other.invalid.length ? `Not a profile ID: ${other.invalid.join(', ')}` : undefined}>
            <Textarea rows={2} spellCheck={false} value={otherText} onChange={(e) => setOtherText(e.target.value)} />
          </Field>
        ) : (
          <p className="hint">Only users of content test groups can get a test. Sending to other profiles needs the messages.test_send.any_profile permission.</p>
        )}
      </fieldset>
      {tooMany && <p role="alert">A test send reaches at most {MAX_RECIPIENTS} users.</p>}
      <div className="toolbar">
        <Button variant="primary" onClick={() => body && send.mutate(body)} loading={send.isPending} disabled={!body}>
          Send test
        </Button>
        <Button onClick={() => previewBody && preview.mutate(previewBody)} loading={preview.isPending} disabled={!previewBody}>
          Preview with overrides
        </Button>
        {!source && <span className="hint">Nothing to send yet.</span>}
        {source && !groupIds.length && !allProfiles.length && <span className="hint">Choose a group or a user.</span>}
      </div>
      {send.isError && <p role="alert">{sendErrorText(send.error)}</p>}
      {send.data && !send.isError && <ResultsTable response={send.data} labelOf={labelOf} />}
      {preview.isError && <p role="alert">{preview.error instanceof ApiError ? preview.error.message : 'The preview could not be rendered.'}</p>}
      {previewed && !preview.isError && (
        <section aria-label="Preview with overrides">
          <p className="hint">
            Rendered for user <code>{labelOf(previewed.profileId)}</code> with the attribute overrides and samples above.
          </p>
          <HumanView channel={channel} result={previewed} />
        </section>
      )}
    </div>
  );
}

/**
 * Test send and preview links of one message (BB-59): attribute overrides and trigger data samples shared by both,
 * then «Send test» — content test groups and their users (other profiles only with
 * `messages.test_send.any_profile`), a per-recipient result — and «Preview links» for colleagues (push and cards).
 */
export function MessageTestPanel({ environmentId, channels, contentOf, campaignId, variantApiId, samples }: MessageTestPanelProps) {
  const permissions = usePermissions();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<TestSendChannel>(channels[0] ?? 'push_android');
  const [overridesText, setOverridesText] = useState('');
  const [sampleText, setSampleText] = useState<Partial<Record<PreviewSample, string>>>({});
  const [tab, setTab] = useState('send');
  const variables = useTemplateVariables(environmentId, campaignId);
  const channel = channels.includes(picked) ? picked : (channels[0] ?? picked);
  if (!channels.length) return null;

  const shownSamples = samples ?? SAMPLE_ORDER.filter((s) => variables.data?.namespaces.includes(s));
  const overrides = parseJsonObject(overridesText);
  const parsed = shownSamples.map((s) => ({ sample: s, ...parseJsonObject(sampleText[s] ?? '') }));
  const invalid = Boolean(overrides.error) || parsed.some((p) => p.error);
  const sampleValues = Object.fromEntries(parsed.flatMap((p) => (p.value ? [[p.sample, p.value]] : []))) as Partial<Record<PreviewSample, Record<string, unknown>>>;
  const content = contentOf?.(channel);
  const draft: PreviewLinkSource | null = content ? { draftContent: content } : campaignId ? { messageRef: { campaignId, ...(variantApiId ? { variantApiId } : {}) } } : null;
  const source = invalid ? null : draft;
  // A campaign's link renders its saved variant, so it is listed for the campaign and can be revoked there.
  const linkSource: PreviewLinkSource | null = invalid ? null : campaignId ? { messageRef: { campaignId, ...(variantApiId ? { variantApiId } : {}) } } : draft;
  const allowed = permissions.has('messages.test_send');

  return (
    <section className="panel message-test" aria-label="Test send">
      <div className="toolbar">
        <h3>Test</h3>
        <Button size="sm" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
          {open ? 'Hide test send' : 'Test send and preview links'}
        </Button>
      </div>
      {open && !allowed && <p className="hint">{permissions.isPending ? 'Checking your permissions…' : 'Test send and preview links need the messages.test_send permission.'}</p>}
      {open && allowed && (
        <div className="stack-form">
          {channels.length > 1 && (
            <Field label="Test channel">
              <Select options={channels.map((c) => ({ value: c, label: CHANNEL_LABELS[c] }))} value={channel} onChange={(e) => setPicked(e.target.value as TestSendChannel)} />
            </Field>
          )}
          <Field label="Attribute overrides (JSON)" hint="Values used only to render this test, e.g. a first name or a custom attribute; the profile is not changed." error={overrides.error}>
            <Textarea rows={3} spellCheck={false} value={overridesText} placeholder='{"first_name": "Test", "custom.tier": "vip"}' onChange={(e) => setOverridesText(e.target.value)} />
          </Field>
          {parsed.length > 0 && (
            <fieldset>
              <legend>Trigger data samples</legend>
              {parsed.map((p) => (
                <Field key={p.sample} label={SAMPLE_FIELDS[p.sample].label} error={p.error}>
                  <Textarea rows={2} spellCheck={false} value={sampleText[p.sample] ?? ''} placeholder='{"symbol": "EURUSD"}' onChange={(e) => setSampleText((was) => ({ ...was, [p.sample]: e.target.value }))} />
                </Field>
              ))}
            </fieldset>
          )}
          <Tabs
            label="Test"
            value={PREVIEW_LINK_CHANNELS.includes(channel) ? tab : 'send'}
            onValueChange={setTab}
            items={[
              { value: 'send', label: 'Send test', content: <TestSendForm environmentId={environmentId} channel={channel} source={source} {...(overrides.value ? { overrides: overrides.value } : {})} samples={sampleValues} anyProfile={permissions.has('messages.test_send.any_profile')} /> },
              ...(PREVIEW_LINK_CHANNELS.includes(channel)
                ? [
                    {
                      value: 'links',
                      label: 'Preview links',
                      content: (
                        <PreviewLinksSection
                          environmentId={environmentId}
                          channel={channel as PreviewLinkChannel}
                          source={linkSource}
                          {...(overrides.value ? { overrides: overrides.value } : {})}
                          samples={Object.fromEntries(Object.entries(sampleValues).map(([s, v]) => [SAMPLE_FIELDS[s as PreviewSample].send, v]))}
                          {...(campaignId ? { campaignId } : {})}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}
    </section>
  );
}
